import React, { useState } from 'react';
import { useNavigate, Routes, Route, useLocation } from 'react-router-dom';
import DateSelection from './DateSelection';
import GuestInfo from './GuestInfo';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

interface BookingData {
  checkIn: Date | null;
  checkOut: Date | null;
  guests: {
    name: string;
    email: string;
    phone: string;
    guests: number;
  } | null;
}

const BookingWizard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [bookingData, setBookingData] = useState<BookingData>({
    checkIn: null,
    checkOut: null,
    guests: null
  });

  const steps = [
    { id: 1, name: 'Dates', path: '/booking/dates' },
    { id: 2, name: 'Guest Info', path: '/booking/guest-info' },
    { id: 3, name: 'Review', path: '/booking/review' }
  ];

  const currentStep = steps.findIndex(step => step.path === location.pathname) + 1;

  const handleNext = () => {
    const nextStep = steps[currentStep];
    if (nextStep) {
      navigate(nextStep.path);
    }
  };

  const handleBack = () => {
    const previousStep = steps[currentStep - 2];
    if (previousStep) {
      navigate(previousStep.path);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Progress Bar */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="relative">
            <div className="overflow-hidden h-2 flex rounded-full bg-gray-200">
              <div
                className="transition-all duration-500 ease-in-out h-full bg-airbnb-red rounded-full"
                style={{ width: `${(currentStep / steps.length) * 100}%` }}
              />
            </div>
            <div className="mt-4 flex justify-between">
              {steps.map((step, index) => (
                <div
                  key={step.id}
                  className={`flex items-center ${
                    index < currentStep ? 'text-airbnb-red' : 'text-gray-500'
                  }`}
                >
                  <span className={`flex items-center justify-center h-8 w-8 rounded-full border-2 ${
                    index < currentStep ? 'border-airbnb-red bg-airbnb-red text-white' : 'border-gray-300'
                  }`}>
                    {index + 1}
                  </span>
                  <span className="ml-2 text-sm font-medium">{step.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-grow max-w-3xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
        <Routes>
          <Route 
            path="/dates" 
            element={
              <DateSelection 
                checkIn={bookingData.checkIn}
                checkOut={bookingData.checkOut}
                onSelect={(dates) => setBookingData(prev => ({ ...prev, ...dates }))}
              />
            } 
          />
          <Route 
            path="/guest-info" 
            element={
              <GuestInfo
                guestData={bookingData.guests}
                onSubmit={(data) => setBookingData(prev => ({ ...prev, guests: data }))}
              />
            }
          />
        </Routes>
      </div>

      {/* Navigation Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <button
            onClick={handleBack}
            disabled={currentStep === 1}
            className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              currentStep === 1 
                ? 'text-gray-400 cursor-not-allowed' 
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <ChevronLeftIcon className="h-5 w-5 mr-1" />
            Back
          </button>
          <button
            onClick={handleNext}
            disabled={currentStep === steps.length}
            className={`flex items-center px-6 py-2 rounded-lg text-sm font-medium transition-colors ${
              currentStep === steps.length
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-airbnb-red text-white hover:bg-red-600'
            }`}
          >
            Next
            <ChevronRightIcon className="h-5 w-5 ml-1" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingWizard;